import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/screens/plan/constants/planDashboardTokens";
const I18n = I18nUtil.createScoped(Translations);
/** Plan Dashboard — layout / card tokens（Figma 91827:51840 · PC Demo `92647:59484`） */
export const PLAN_DASHBOARD_LAYOUT = {
  scrollClass: "flex w-full flex-col gap-6",
  /** PC Demo 滚动区 — 末卡不被吸底 sample 条 + gradient 遮挡（`92647:59692`） */
  scrollPcClass: "flex w-full flex-col gap-6 pb-[152px]",
  demoBannerPcClass: "flex w-full items-center py-0",
  demoBannerH5Class: "mb-2 flex w-full items-center gap-3",
  demoTitleClass: "title_xl text-content_primary",
  demoCloseClass:
    "inline-flex size-6 shrink-0 items-center justify-center text-content_primary",
  moduleStackClass: "flex w-full flex-col gap-6",
} as const;

export const PLAN_DASHBOARD_CARD = {
  /** H5 `91827:51873` · PC `91919:52340` — dark neutral primary */
  shellClassH5:
    "flex w-full flex-col gap-4 rounded-[12px] bg-background_solid_neutral_primary p-3 text-left",
  /** PC `91919:52340` — page bg shows through; no module border (5VinbKL7aygP) */
  shellClassPc: "flex w-full flex-col gap-4 rounded-[12px] p-6 text-left",
  headerRowClass: "flex w-full items-center justify-between gap-2",
  titleClass: "body_medium_s text-content_tertiary",
  dividerClass: "h-px w-full shrink-0 bg-border_inverse_transparent10",
  valueClass: "headline_regular_s tracking-[-0.8px] text-content_primary",
  /** H5 `91827:52028` · PC `91919:52495` */
  subValueClassH5: "body_medium_m text-content_tertiary",
  subValueClassPc: "body_medium_m text-content_tertiary",
  portfolioValueSummaryClass: "flex flex-col gap-1",
  /** PC `92647:59904` — value + subtitle 纵向 */
  portfolioValueSummaryPcClass: "flex flex-col gap-1",
  /** @deprecated use PLAN_DASHBOARD_PORTFOLIO_LAYOUT.chartRowClass */
  portfolioChartRowPcClass: "flex w-full items-center justify-center",
  /** @deprecated use PLAN_DASHBOARD_PORTFOLIO_LAYOUT.legendColClass */
  portfolioLegendColPcClass: "min-w-0 flex-1",
  /** PC `92658:54123` — 数值区 ↔ 图表 */
  netWorthAreaBodyPcClass: "flex flex-col gap-2",
  netWorthAreaValueBlockClass: "flex flex-col gap-1",
  /** Metric/Delta `100551:362753` */
  netWorthDeltaRowClass: "flex items-center gap-1",
  /** Delta/Value `100551:362754` */
  netWorthDeltaValueClass: "inline-flex shrink-0 items-center gap-1",
  /** content/accent1/primary — positive */
  changeClass: "body_medium_m text-content_accent1_primary",
  /** content/accent2/primary — negative */
  changeNegativeClass: "body_medium_m text-content_accent2_primary",
  /** body_medium_s · content/tertiary — range meaning `100551:362757` */
  periodClass: "body_medium_s text-content_tertiary",
  chartInsetClass: "flex w-full flex-col gap-2.5 rounded-[12px] p-2",
  /**
   * Card/NetWorth `100551:362748`
   * title_m / content_primary · 无 header divider · p-16 gap-12
   */
  netWorthShellH5Class:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-4 text-left",
  netWorthShellPcClass:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-6 text-left",
  netWorthTitleClass: "title_m text-content_primary",
  netWorthBodyClass: "flex w-full flex-col gap-2",
  netWorthFootnoteClass: "body_s w-full text-content_quaternary",
} as const;

/**
 * Card/PortfolioDistribution H5 `100551:362990` · PC `100654:398741`
 * — title_m · 无 header divider · donut+legend 横排 · footer CTA
 */
export const PLAN_DASHBOARD_PORTFOLIO_LAYOUT = {
  shellH5Class:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-4 text-left",
  shellPcClass:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-6 text-left",
  titleClass: "title_m text-content_primary",
  metricClass: "flex flex-col gap-1",
  valueClass: "headline_regular_s tracking-[-0.8px] text-content_primary",
  subtitleClass: "body_medium_m text-content_tertiary",
  chartRowClass: "flex w-full items-center justify-center",
  legendColClass: "flex min-w-0 flex-1 flex-col items-start",
  footerClass:
    "flex w-full flex-col items-start border-t border-border_inverse_transparent10 pt-3",
  footerLinkClass:
    "inline-flex items-center gap-1 body_medium_m text-content_brand_primary",
  /** Figma Donut/Segments 221 — ring fitted from exported paths */
  donutSizePx: 221,
  donutOuterR: 78,
  donutInnerR: 54,
} as const;

/**
 * List cards shared shell — Subscriptions/Payments p-12 · Accounts/Tx p-16
 * Figma `100551:362953` / `363033` / `363067` / `363091`
 */
export const PLAN_DASHBOARD_LIST_CARD = {
  shellP3H5Class:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-3 text-left",
  shellP3PcClass:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-6 text-left",
  shellP4H5Class:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-4 text-left",
  shellP4PcClass:
    "flex w-full flex-col gap-3 rounded-[12px] bg-background_solid_neutral_primary p-6 text-left",
  titleClass: "title_m text-content_primary",
  headerRowClass: "flex w-full items-center justify-between gap-2",
  valueClass: "headline_regular_s tracking-[-0.8px] text-content_primary",
  subtitleClass: "body_medium_m text-content_tertiary",
  metricClass: "flex flex-col gap-1",
  rowsClass: "flex w-full flex-col",
  /** gap-14 between account progress rows */
  accountRowsClass: "flex w-full flex-col gap-3.5",
  rowClass: "flex w-full items-center py-3",
  rowWithAvatarClass: "flex w-full items-center gap-2 py-3",
  txRowClass: "flex w-full items-center justify-between gap-3 py-3",
  rowLabelsClass: "flex min-w-0 flex-1 flex-col items-start",
  rowTitleClass: "body_medium_m truncate text-content_primary",
  /** Tx merchant title is body_medium_s in Figma */
  txTitleClass: "body_medium_s truncate text-content_primary",
  rowMetaClass: "body_s truncate text-content_tertiary",
  txMetaClass: "body_s truncate text-content_tertiary",
  txMetaPendingClass: "body_s truncate text-content_secondary",
  txContentClass: "flex min-w-0 flex-1 items-center gap-3",
  rowAmountClass: "body_m shrink-0 text-content_primary",
  avatarClass:
    "relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background_white_overlay10 body_medium_s text-content_primary",
  avatarImgClass: "size-full object-cover",
  avatarFallbackClass: "body_medium_s text-content_primary",
  accountRowClass: "flex w-full flex-col gap-1.5",
  accountRowTextClass: "flex w-full items-center justify-between gap-2",
  accountLabelClass: "body_medium_m text-content_primary",
  accountAmountClass: "body_m shrink-0 text-content_primary",
  footnoteClass: "body_s w-full text-content_tertiary",
} as const;

/** Legend / donut segment colors — Figma variable order */
export const PLAN_DASHBOARD_PORTFOLIO_COLOR_CLASSES = [
  "text-content_brand_primary",
  "text-content_accent4_primary",
  "text-content_accent5_primary",
  "text-content_aura_lv1_primary",
  "text-content_aura_lv2_primary",
  "text-content_tertiary",
] as const;

export const PLAN_DASHBOARD_PORTFOLIO_DOT_CLASSES = [
  "bg-content_brand_primary",
  "bg-content_accent4_primary",
  "bg-content_accent5_primary",
  "bg-content_aura_lv1_primary",
  "bg-content_aura_lv2_primary",
  "bg-content_tertiary",
] as const;

export const PLAN_DASHBOARD_SPEND_ROW = {
  /** legacy demo icon box — Card/SpendByCategory Figma 无底框 */
  iconBoxClass:
    "inline-flex size-6 shrink-0 items-center justify-center rounded-[6px] bg-background_white_overlay5",
  /** Figma Bar track `100551:362840` — border/inverse/transparent10 · h-6 · rounded-10 */
  barTrackClass:
    "h-1.5 w-full overflow-hidden rounded-[10px] bg-border_inverse_transparent10",
  barFillClass: "h-full rounded-[10px] bg-content_brand_primary",
  labelClass: "body_medium_m text-content_primary",
  amountClass: "body_m text-content_primary",
  stackedBarClass: "flex h-5 w-full gap-px overflow-hidden rounded-[4px]",
  monthLabelClass: "body_m text-content_primary",
  /** Chip/Account `100551:362824` */
  rangeChipClass:
    "inline-flex shrink-0 cursor-pointer items-center overflow-clip rounded-[6px] bg-background_white_overlay5 px-2 py-1.5 body_medium_s text-content_primary",
  rowClass: "flex w-full items-center py-3",
  rowContentClass: "flex min-w-0 flex-1 flex-col gap-1.5",
  rowTextClass: "flex w-full items-center justify-between gap-2",
  rowLabelClass: "flex min-w-0 items-center gap-1",
} as const;

/** TimeRangeDropdown Open `100551:390910` */
export const PLAN_DASHBOARD_CATEGORY_RANGE_DROPDOWN = {
  panelClass:
    "absolute right-0 top-[calc(100%+4px)] z-20 flex min-w-[180px] flex-col overflow-clip rounded-lg border border-border_inverse_transparent10 bg-background_solid_neutral_primary p-2 shadow-[0px_0px_32px_0px_rgba(0,0,0,0.05)]",
  itemClass:
    "flex h-10 w-full cursor-pointer items-center justify-between overflow-clip p-2 body_medium_m text-content_primary",
  checkClass: "size-5 shrink-0 text-content_primary",
} as const;

export const PLAN_DASHBOARD_TIME_TABS = {
  /** Chart/RangeTabs `100551:362758` — equal flex + pill active */
  areaRowClass: "flex w-full items-start",
  areaTabClass:
    "inline-flex flex-1 min-w-px flex-col items-center justify-center rounded-lg p-2 body_medium_s text-content_tertiary",
  areaActiveTabClass:
    "inline-flex flex-1 min-w-px flex-col items-center justify-center rounded-lg bg-background_white_overlay5 p-2 body_medium_s text-content_primary",
  /** 双线 `91827:52010` */
  dualRowClass: "flex w-full items-center justify-between pl-[33px]",
  dualTabClass:
    "inline-flex h-5 w-8 items-center justify-center body_xs text-content_quaternary",
  dualActiveTabClass:
    "inline-flex items-center justify-center rounded-lg bg-background_brand_overlay5 px-1 py-0.5 body_medium_xs text-content_brand_primary",
} as const;

/** Chart/Price `100551:362773` — Y 轴 / 网格 / 面积 / X 轴 */
export const PLAN_DASHBOARD_AREA_CHART = {
  /** Plot ↔ XAxis：Figma gap 8px，略加大避免填充视觉压住时间 */
  rootClass: "flex w-full flex-col gap-3 overflow-visible",
  plotClass: "relative w-full overflow-hidden",
  gridStackClass:
    "pointer-events-none absolute inset-0 z-[1] flex flex-col justify-between",
  gridRowClass: "flex w-full items-center gap-1",
  yLabelClass: "body_xs w-9 shrink-0 text-content_quaternary",
  gridLineClass:
    "h-0 min-w-0 flex-1 border-t border-dashed border-border_inverse_transparent10",
  plotSvgClass: "text-content_brand_primary",
  xAxisClass: "flex w-full items-center justify-between pl-[33px]",
  xLabelClass:
    "inline-flex h-5 w-8 shrink-0 items-center justify-center body_xs text-content_quaternary",
  yLabelWidthPx: 36,
  /** Figma Chart/Plot `100551:362774` height 152 */
  plotHeightPx: 152,
  plotPadTopPx: 4,
  /** 填充底边内边距，与 X 时间轴留白 */
  plotPadBottomPx: 10,
} as const;

/** PC 底栏 `91919:52292` */

/** H5 双线 legend `100551:362897` · secondary row `100551:362907` */
export const PLAN_DASHBOARD_DUAL_LEGEND = {
  stackClass: "flex w-full flex-col items-start",
  rowClass: "flex w-full items-center justify-between rounded-lg",
  labelGroupClass: "flex items-center gap-2",
  labelClass: "body_medium_s text-content_tertiary",
  valuePillClass: "flex items-center py-0.5 rounded-[30px]",
  valueClass: "headline_regular_s tracking-[-0.8px] text-content_primary",
  /** 3-month average amount — Figma `100551:362914` */
  secondaryValueClass: "body_medium_s text-content_secondary",
  primaryValueGroupClass: "flex items-center gap-1",
  /** +$260 — Figma `100551:362906` content/secondary */
  deltaClass: "body_medium_s text-content_secondary",
  dotPrimaryClass: "bg-content_brand_primary",
  dotSecondaryClass: "bg-content_tertiary",
} as const;

/** Spend dual chart — Chart/Price `100551:362915` */
export const PLAN_DASHBOARD_DUAL_CHART = {
  rootClass: "flex w-full flex-col gap-2 overflow-clip",
  plotClass: "relative w-full",
  plotHeightPx: 150,
  yLabelWidthPx: 36,
  primaryStrokeClass: "text-content_brand_primary",
  secondaryStrokeClass: "text-content_tertiary",
  cursorStrokeClass: "text-content_quaternary",
  /** Cursor markers — Figma Ellipse `100551:362939` / `100551:362940` (4×4) */
  markerSizePx: 4,
  markerLayerClass: "pointer-events-none absolute inset-y-0",
  /** This month — fill `background/solid/neutral/secondary` · stroke `border/brand/solid` */
  markerPrimaryClass:
    "absolute size-1 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border_brand_solid bg-background_solid_neutral_secondary",
  /** 3-month average — fill `background/solid/neutral/secondary` · stroke `background/white/overlay100%` */
  markerSecondaryClass:
    "absolute size-1 -translate-x-1/2 -translate-y-1/2 rounded-full border border-background_white_overlay100 bg-background_solid_neutral_secondary",
} as const;

/** H5/PC Portfolio legend `100551:363006` · `100654:398757` */
export const PLAN_DASHBOARD_PORTFOLIO_LEGEND = {
  rowClass:
    "flex w-full shrink-0 items-center justify-between rounded-lg px-3 py-1",
  rowClassPc:
    "flex w-full shrink-0 items-center justify-between rounded-lg px-3 py-1",
  labelClass: "body_s text-content_primary",
  amountClass: "body_s shrink-0 text-content_primary",
  itemClass: "flex shrink-0 items-center gap-2",
} as const;

/**
 * Lock overlay — H5 Unpaid `100654:396668` UpgradeCard /
 * Paid-no-accounts `100551:362489` UpgradeCard
 *
 * H5：相对 MainTabs fixed 钉底（同 `LinkAccountBar` h5-pinned：
 * `z-[810]` + `bottom: var(--mobile-bottom-height)`）。遮挡态 Demo 为死预览，不加 spacer。
 * PC：sticky 钉在 `plan-paid-scroll` 可视区正中（`100cqh`），不含侧边栏；滚动时保持悬浮。
 */
export const PLAN_DASHBOARD_LOCK_OVERLAY = {
  /** H5 — fixed 钉在 MainTabs 上方；`bottom` 由组件 style 注入 */
  rootH5Class:
    "pointer-events-none fixed left-0 right-0 z-[810] flex justify-center px-4",
  /**
   * PC — sticky 相对 plan-paid-scroll 内容列居中。
   * `h-[100cqh]` = 滚动视口高；`-mb-[100cqh]` 不占文档流，背景可从下方穿过。
   */
  rootPcClass:
    "pointer-events-none sticky top-0 z-10 mb-[-100cqh] flex h-[100cqh] w-full items-center justify-center",
  /** 对齐 LinkAccountBar H5 全宽 / PC `md:w-480px`（旧 UpgradeCard 为 361） */
  cardWrapClass: "pointer-events-auto w-full max-w-[480px]",
  cardWrapPcClass: "pointer-events-auto w-full max-w-[480px] px-4",
  /** LinkAccountBar 同款：贴 MainTabs 顶边 */
  h5BottomStyle: {
    bottom: "var(--mobile-bottom-height, 116px)",
  } as const,
  unpaidCardClass:
    "flex w-full flex-col items-start justify-center gap-3 rounded-[12px] bg-background_solid_neutral_primary p-4",
  paidCardClass:
    "flex w-full flex-col items-start justify-center gap-3 rounded-[12px] bg-background_brand_overlay100 p-4",
  copyStackClass: "flex w-full flex-col gap-1",
  unpaidTitleClass: "title_l text-content_primary",
  unpaidBodyClass: "body_m w-full text-content_secondary",
  paidTitleClass: "title_l text-content_inverse_primary",
  paidBodyClass: "body_m w-full text-content_inverse_secondary",
  /** Figma: two CTAs row · spacing 12 (UABwrNcWba1x) */
  actionsClass: "flex w-full flex-row flex-wrap items-center gap-3",
  upgradeCtaClass:
    "button_m inline-flex min-w-20 cursor-pointer items-center justify-center rounded-lg bg-background_accent4_overlay100 px-3 py-2 text-content_inverse_primary",
  /** Connect Plaid fill #000000 (UABwrNcWba1x) */
  connectCtaClass:
    "button_m inline-flex min-w-20 cursor-pointer items-center justify-center gap-0.5 rounded-lg bg-black px-3 py-2 text-content_inverse_primary",
  screenshotCtaClass:
    "button_m inline-flex min-w-20 cursor-pointer items-center justify-center rounded-lg bg-background_inverse_overlay100 px-3 py-2 text-content_primary",
  plaidLogoClass: "size-5 shrink-0 object-cover",
  demoBackdropClass: "pointer-events-none relative opacity-30",
} as const;

/** FilterBar — Connected H5 `100551:390699` Section/FilterBar */
export const PLAN_DASHBOARD_FILTER_BAR = {
  rootClass: "flex w-full items-center justify-between px-4 py-0",
  rootPcClass: "flex w-full items-center justify-between px-0 pb-0 pt-6",
  toggleGroupClass: "flex items-center gap-2",
  toggleTrackOffClass:
    "inline-flex w-9 shrink-0 cursor-pointer items-center overflow-hidden rounded-full border-[1.5px] border-background_white_overlay20 p-1",
  /** ON: solid brand track + white knob (eCQ1ABwHJxl7 / Figma FilterBar) */
  toggleTrackOnClass:
    "inline-flex w-9 shrink-0 cursor-pointer items-center justify-end overflow-hidden rounded-full border-[1.5px] border-content_brand_primary bg-content_brand_primary p-1",
  toggleKnobClass: "size-3 shrink-0 rounded-full bg-background_white_overlay20",
  toggleKnobOnClass:
    "size-3 shrink-0 rounded-full bg-background_solid_neutral_primary",
  toggleLabelClass: "body_medium_s text-content_tertiary",
  manageClass:
    "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-background_solid_neutral_primary px-3 py-2",
  manageLabelClass: "body_medium_s text-content_primary",
  disabledClass: "pointer-events-none opacity-30",
} as const;

/** Empty module card — Figma H5 `100551:390817` / PC Grid `100712:132542` */
export const PLAN_DASHBOARD_EMPTY_CARD = {
  /** 卡壳：p-16 · gap-12 · 无 divider（空态帧与 filled Card 不同） */
  shellClass:
    "flex w-full flex-col gap-3 overflow-hidden rounded-[12px] bg-background_solid_neutral_primary p-4",
  /** PC 八卡一律 220（含 Add） */
  shellHeightPcClass: "h-[220px]",
  /** H5 通用卡 220 */
  shellHeightH5Class: "h-[220px]",
  /** H5 含 Add 卡 284 */
  shellHeightH5WithAddClass: "h-[284px]",
  cardTitleClass: "title_m shrink-0 text-content_primary",
  /** 标题行可挂 range / frequency 下拉（EMPTY 仍可切换） */
  headerRowClass: "flex w-full shrink-0 items-center justify-between gap-2",
  bodyClass: "flex min-h-0 flex-1 flex-col gap-2",
  metricClass:
    "headline_regular_s shrink-0 tracking-[-0.8px] text-content_primary",
  emptyStateClass:
    "flex w-full flex-1 flex-col items-center justify-center gap-1 text-center",
  emptyStateWithAddClass:
    "flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-4 text-center",
  copyStackClass: "flex w-full flex-col gap-1",
  titleClass: "body_medium_m w-full text-content_secondary",
  subtitleClass: "body_s w-full text-content_tertiary",
  addRowClass:
    "inline-flex cursor-pointer items-center justify-center gap-2.5 rounded-[12px] border border-dashed border-border_inverse_transparent20 p-3",
  addIconWrapClass:
    "inline-flex size-6 shrink-0 items-center justify-center rounded-[12px] bg-background_brand_overlay20",
  addLabelClass: "button_s text-content_brand_primary",
} as const;

/**
 * Module loading / error — Figma Loading `101498:23601` · Error `101498:23688`
 * 与 Empty 同壳：title_m + 内容区居中；固定高 220。
 */
export const PLAN_DASHBOARD_MODULE_STATUS = {
  shellClass:
    "flex w-full flex-col gap-3 overflow-hidden rounded-[12px] bg-background_solid_neutral_primary p-4",
  shellHeightClass: "h-[220px]",
  cardTitleClass: "title_m shrink-0 text-content_primary",
  bodyClass: "flex min-h-0 w-full flex-1 flex-col items-center justify-center",
  errorBodyClass:
    "flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-4 py-10 text-center",
  copyStackClass: "flex w-full flex-col gap-1",
  errorTitleClass: "body_medium_m w-full text-content_secondary",
  errorSubtitleClass: "body_s w-full text-content_tertiary",
  retryBtnClass:
    "button_m inline-flex h-11 min-w-20 cursor-pointer items-center justify-center rounded-lg bg-background_brand_overlay10 px-4 py-3 text-content_brand_primary",
  spinnerClass:
    "size-6 shrink-0 animate-spin rounded-full border-2 border-content_tertiary border-t-content_primary",
  errorTitleDefault: I18n.t("Couldn't load"),
  errorSubtitleDefault: I18n.t("Please try again later."),
  retryLabel: I18n.t("Retry"),
} as const;

export const PLAN_DASHBOARD_MODULE_STACK = {
  stackClass: "flex w-full flex-col gap-6",
  /** H5 Empty Section/Cards `100551:390817` — gap 12 */
  stackEmptyH5Class: "flex w-full flex-col gap-3",
  /**
   * PC 流式双列 — Figma `Column/Left` + `Column/Right`（`100712:132542`）。
   * 禁止 `grid-cols-2`：同行等高会在矮卡下方留白。
   */
  stackPcClass: "flex w-full items-start gap-6",
  columnClass: "flex min-w-0 flex-1 flex-col gap-6",
} as const;

/** ReconnectAlert — Figma Auth `100551:390653` get_variable_defs */
export const PLAN_DASHBOARD_RECONNECT_ALERT = {
  rootClass:
    "flex w-full items-start gap-3 rounded-[12px] border border-border_accent3_transparent50 bg-background_accent3_overlay10 p-3",
  rootPcClass:
    "flex w-full items-center gap-4 rounded-[12px] border border-border_accent3_transparent50 bg-background_accent3_overlay10 p-4",
  logosClass: "relative flex h-10 w-[52px] shrink-0 items-center",
  logoClass:
    "absolute size-10 overflow-hidden rounded-full border-2 border-background_solid_neutral_secondary bg-background_solid_neutral_primary",
  overflowClass:
    "absolute left-5 inline-flex size-10 items-center justify-center rounded-full border-2 border-background_solid_neutral_secondary bg-background_solid_neutral_primary body_medium_s text-content_primary",
  textColClass: "min-w-0 flex-1 flex flex-col gap-0.5",
  titleClass: "title_l text-content_primary",
  bodyClass: "body_s text-content_secondary",
  ctaClass:
    "inline-flex shrink-0 items-center justify-center rounded-full bg-background_accent4_overlay100 px-4 py-2 button_m text-content_inverse_primary",
} as const;
